<template>
  <div>
    <FiltersSection
      v-if="categories.length && isAuthenticated"
      data-testid="analysis-filters"
      :search="search"
      :categories="categories"
      :activated-categories="activeCategories"
      with-search
      @search="searchArticles"
      @change-categories="changeCategories"
    />
    <div class="relative" :class="isAuthenticated ? 'mt-8' : ''">
      <div v-if="loading" class="absolute left-0 top-0 w-full">
        <TheLoader data-testid="analysis-loading-spinner" />
      </div>
      <div v-else data-testid="analysis-content">
        <div
          v-if="noResultsFound || noResultsSearch || noResultsFilter"
          class="mx-auto flex w-full max-w-full flex-col items-center justify-center gap-5 pt-20 text-center text-xl font-semibold text-s-700 md:max-w-[580px]"
        >
          <ArticleAnalysisIcon
            class="h-full max-h-[112px] min-h-[112px] w-full min-w-[126px] max-w-[126px]"
          />
          <span v-if="noResultsFound">
            There are currently no
            <strong class="font-bold text-black">{{ serviceName }}</strong>
            articles published, please check back next month for newly posted
            articles!
          </span>
          <span v-if="noResultsFilter">
            Sorry, we couldn’t find any results for the selected filters
          </span>
          <span v-if="noResultsSearch">
            Sorry, we couldn’t find any results for
            <strong class="font-bold text-black"> "{{ search }}".</strong>
          </span>
        </div>

        <div v-if="!noResultsFound">
          <div v-if="!isAuthenticated">
            <div class="grid xl:grid-cols-[1fr_450px] gap-6 xl:gap-3">
              <div class="grid grid-cols-1 gap-4">
                <ArticleCard
                  v-for="article in principalArticles"
                  :key="article.slug"
                  :title="article.title"
                  :date="article.date"
                  :excerpt="article.excerpt"
                  :categories="article[serviceCategoryName]"
                  :service-base-path="serviceBasePath"
                  :slug="article.slug"
                  :cover-url="article.backgroundImage"
                  :service="slug"
                  :query-string="queryString"
                />
              </div>
              <div
                class="hidden md:grid max-h-[590px] grid-cols-1 gap-4 overflow-auto pr-2"
              >
                <ArticleCard
                  v-for="article in scrollArticles"
                  :key="article.slug"
                  :title="article.title"
                  :date="article.date"
                  :excerpt="article.excerpt"
                  :categories="article[serviceCategoryName]"
                  :service-base-path="serviceBasePath"
                  :slug="article.slug"
                  :cover-url="article.backgroundImage"
                  :service="slug"
                  :query-string="queryString"
                  :show-description="false"
                  class="min-h-[160px]"
                />
              </div>
              <div class="relative w-full overflow-hidden md:hidden">
                <Swiper
                  class="w-[956px] !pb-3 rounded-xl"
                  :slides-per-view="3"
                  :space-between="16"
                  :speed="500"
                >
                  <SwiperSlide
                    v-for="(swArticle, i) in scrollArticles"
                    :key="i"
                    class="w-[308px]"
                  >
                    <ArticleCard
                      :key="swArticle.slug"
                      :title="swArticle.title"
                      :date="swArticle.date"
                      :excerpt="swArticle.excerpt"
                      :categories="swArticle[serviceCategoryName]"
                      :service-base-path="serviceBasePath"
                      :slug="swArticle.slug"
                      :cover-url="swArticle.backgroundImage"
                      :service="slug"
                      :query-string="queryString"
                      :show-description="false"
                      class="min-h-[160px]"
                    />
                  </SwiperSlide>
                  <SwiperSlide class="w-[308px]"></SwiperSlide>
                  <SwiperSlide class="w-[308px]"></SwiperSlide>
                </Swiper>
              </div>
            </div>
          </div>
          <div v-else class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ArticleCard
              v-for="article in articles"
              :key="article.slug"
              :title="article.title"
              :date="article.date"
              :excerpt="article.excerpt"
              :categories="article[serviceCategoryName]"
              :service-base-path="serviceBasePath"
              :slug="article.slug"
              :cover-url="article.backgroundImage"
              :service="slug"
              :query-string="queryString"
            />
          </div>
        </div>

        <div
          v-if="hasNextPage && !noResultsFound && isAuthenticated"
          class="mt-6 flex items-center justify-center"
        >
          <Button
            data-testid="load-more-button"
            class="!px-4 !py-3 text-sm lg:!w-full lg:!max-w-40"
            variant="primary"
            :disabled="loadMoreLoading"
            :show-spinner="loadMoreLoading"
            extra-classes=""
            target="_self"
            type="button"
            :skip-width-classes="false"
            @click="loadMoreArticles"
          >
            Load more
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { SwiperSlide, Swiper } from 'swiper/vue'
import 'swiper/css'
import uniqBy from 'lodash/uniqBy'
import { camelAndPascalCase } from '~/utils/string'
import { ReportImageGenerator } from '~/src/utils/reports/image'

import ArticleAnalysisIcon from '~/assets/icons/article-analysis.svg'
import ArticleCard from '~/components/price-assessments/ArticleCard.vue'
import Button from '~/components/common/Button.vue'
import FiltersSection from '~/components/price-assessments/FiltersSection.vue'
import TheLoader from '~/components/TheLoader.vue'
import type { TGeneral } from '~/src/types/common'
import useAuthStore from '~/stores/auth'

const props = withDefaults(
  defineProps<{
    name: string
    slug: string | string[]
    serviceBasePath?: string | null
  }>(),
  {
    serviceBasePath: null,
  },
)
const { $graphqlClient, $rudderstack } = useNuxtApp()
const search = ref<string>('')
const activeCategories = ref<TGeneral[]>([])
const loading = ref<boolean>(true)
const loadMoreLoading = ref<boolean>(false)
const articles = ref<TGeneral[]>([])
const categories = ref<TGeneral[]>([])
const endCursor = ref<string | null>(null)
const hasNextPage = ref<boolean>(true)
const queryString = ref<Record<string, any>>({})
const authStore = useAuthStore()

const isAuthenticated = computed(() => authStore.authenticated)
const serviceCategoryName = computed(() => {
  const { strLower } = camelAndPascalCase(props.slug)
  return `${strLower}Categories`
})
const serviceName = computed(() => props.name)

const noResultsFound = computed(
  () =>
    activeCategories.value.length === categories.value.length &&
    search.value === '' &&
    !articles.value.length &&
    !loading.value,
)
const noResultsSearch = computed(
  () => search.value !== '' && !articles.value.length && !loading.value,
)
const noResultsFilter = computed(
  () =>
    activeCategories.value.length !== categories.value.length &&
    search.value === '' &&
    !articles.value.length &&
    !loading.value,
)
const principalArticles = computed(() => articles.value.slice(0, 2))
const scrollArticles = computed(() => articles.value.slice(2))

const setArticleBackgrounds = (articlesList: TGeneral[]) => {
  if (
    activeCategories.value.length === categories.value.length &&
    search.value === ''
  ) {
    ReportImageGenerator.default.reset()
  }

  articlesList.forEach((article: TGeneral) => {
    article.backgroundImage = ReportImageGenerator.default.next()
  })
}

async function fetchArticles(): Promise<void> {
  const selectedCategories =
    activeCategories.value.length > 0
      ? activeCategories.value.map((c: TGeneral) => c.slug)
      : categories.value.map((c: TGeneral) => c.slug)

  const response = await $graphqlClient.getAnalysisArticles({
    service: props.slug,
    categories: selectedCategories,
    search: search.value,
    first: 6,
    after: endCursor.value,
  })

  if (response) {
    const articlesList = response.data.articles.nodes
    articles.value = uniqBy([...articles.value, ...articlesList], 'slug')
    setArticleBackgrounds(articles.value)
    hasNextPage.value = response.data.articles.pageInfo.hasNextPage
    endCursor.value = response.data.articles.pageInfo.endCursor
  }
}

async function fetchCategories(): Promise<void> {
  const response = await $graphqlClient.getAnalysisCategories({
    service: props.slug,
  })

  categories.value = response?.data[serviceCategoryName.value]?.nodes.map(
    (category: TGeneral) => ({
      name: category.name,
      slug: category.slug,
    }),
  )

  activeCategories.value = [...categories.value]
}

function clearArticles(): void {
  articles.value = []
  endCursor.value = null
  hasNextPage.value = true
}

async function changeCategories(newCategories: TGeneral[]): Promise<void> {
  activeCategories.value = newCategories
  clearArticles()
  loading.value = true
  await fetchArticles()
  loading.value = false
}

async function searchArticles(newSearch: string): Promise<void> {
  search.value = newSearch
  clearArticles()
  loading.value = true
  await fetchArticles()
  loading.value = false

  $rudderstack.trackSearchDone({
    search_query: newSearch,
    search_results_count: articles.value.length,
    search_type: 'analysis',
  })
}

async function loadMoreArticles(): Promise<void> {
  if (!hasNextPage.value) return
  loadMoreLoading.value = true
  await fetchArticles()
  loadMoreLoading.value = false
}

onMounted(async () => {
  loading.value = true
  await fetchCategories()
  await fetchArticles()
  loading.value = false
})
</script>
